import {BaseTileProvider} from './base-tile-provider';
import {MapType} from '../../enums/map-type';
import {sprintf} from 'sprintf-js';
import {MapProvider} from '../../enums/map-provider.enum';
import {SettingsService} from '../../services/settings.service';
import {BingMapsTileProvider} from './bing-maps-tile-provider';
import {MapboxTileProvider} from './mapbox-tile-provider';
import {TomTomTileProvider} from './tom-tom-tile-provider';
import {HereMapsTileProvider} from './here-maps-tile-provider';
import {OpenStreetMapsTileProvider} from './open-street-maps-tile-provider';
import {OpenCycleMapTileProvider} from './open-cycle-map-tile-provider';
import {ArcGisTileProvider} from './arc-gis-tile-provider';
import {WorldStreetMapTileProvider} from './world-street-map-tile-provider';
import {UsgsTileProvider} from './usgs-tile-provider';
import {GoogleMapsTileProvider} from './google-maps-tile-provider';
import {Logger} from '../../../common/utils/logger';
import {TemplateTileProvider} from './template-tile-provider';
import {MAP_PROVIDER_DEFS} from './provider-registry';
import {MAP_SOURCE_BY_ID, mapSourceAttribution} from './map-source-catalog';

export class TileProvider extends BaseTileProvider {

  static ID = 'awesome_tiles';

  private providers: Map<string,BaseTileProvider> = new Map<string,BaseTileProvider>()

  constructor(private settings: SettingsService) {
    super();
    this.providers.set(MapProvider.BING_MAPS, new BingMapsTileProvider());
    this.providers.set(MapProvider.MAPBOX, new MapboxTileProvider());
    this.providers.set(MapProvider.TOMTOM, new TomTomTileProvider());
    // HERE on its Raster Tile API v3, only with a key in the environment
    // (hereApiKey). No MapQuest: its open tile servers are shut down. A
    // setting saved with a source that is not registered gets Google's
    // (activeProvider).
    if (HereMapsTileProvider.isConfigured()) {
      this.providers.set(MapProvider.HERE_MAPS, new HereMapsTileProvider(settings));
    }
    this.providers.set(MapProvider.OSM_STANDARD, new OpenStreetMapsTileProvider());
    this.providers.set(MapProvider.OPEN_CYCLE_MAP, new OpenCycleMapTileProvider());
    this.providers.set(MapProvider.ARC_GIS, new ArcGisTileProvider());
    // No Yandex: its tiles are on the ellipsoidal Mercator grid (EPSG:3395),
    // so on this Web Mercator map they sat about 20 km off the true position.
    this.providers.set(MapProvider.ARCGIS_WORLD_STREET_MAP, new WorldStreetMapTileProvider());
    this.providers.set(MapProvider.USGS, new UsgsTileProvider());
    this.providers.set(MapProvider.DEFAULT, new GoogleMapsTileProvider(settings));
    this.providers.set(MapProvider.GOOGLE_MAPS, new GoogleMapsTileProvider(settings));

    // The national agencies. Adding one is a entry in the registry, not a
    // class and a line here.
    MAP_PROVIDER_DEFS.forEach(def =>
      this.providers.set(def.id, new TemplateTileProvider(def)));
  }


  getMapProvider(): string {
    if(this.settings){
      return this.settings.data.mapSource;
    }
    return '';
  }

  /**
   * The provider drawing the source in the settings. An id this build has no
   * provider for -- a retired source, or one another app sharing the settings
   * wrote -- gets Google's, rather than a map with no tiles at all.
   */
  private activeProvider(): BaseTileProvider {
    return this.providers.get(this.getMapProvider()) || this.providers.get(MapProvider.GOOGLE_MAPS);
  }

  getMaxZoom(): number {
    return this.activeProvider().getMaxZoom();
  }

  getUrlTemplate(mapType?: MapType): string {
    return this.activeProvider().getUrlTemplate(mapType);
  }

  /**
   * Credit for the layer on screen, or empty when it needs none.
   *
   * Read from the catalog rather than the provider instance so the
   * hand-written ones are covered too -- OpenStreetMap's credit is
   * required by its licence and OSM does not go through the registry.
   */
  getAttribution(): string {
    // By map type too: Esri's and Mapbox's photos credit other companies.
    return mapSourceAttribution(MAP_SOURCE_BY_ID.get(this.getMapProvider()),
      this.settings ? this.settings.data.mapType : undefined);
  }

  getUrl(x, y, zoom, mapType?: MapType): string {
    return this.activeProvider().getUrl(x, y, zoom, mapType);
  }

}
